import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Icon,
  Button,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  FaFileAlt,
  FaCalendarCheck,
  FaPrescriptionBottleAlt,
  FaMoneyBillWave,
  FaClock,
  FaExclamationCircle,
  FaChevronRight
} from 'react-icons/fa';
import { Reveal, RevealGroup, RevealItem } from '../components/motion';
import { PageHero, SectionHeading, IconMark, InkBand } from '../components/premium';

// One card surface for the whole page: white, a hairline edge, the soft
// two-layer shadow, and a slow settle on hover.
const HAIRLINE = 'rgba(14,47,58,0.06)';
const card = {
  bg: 'white',
  borderRadius: '24px',
  boxShadow: 'card',
  border: '1px solid',
  borderColor: HAIRLINE,
};
const cardHover = {
  transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)',
  _hover: { transform: 'translateY(-3px)' },
};

const PatientPortal = () => {
  const portalFeatures = [
    {
      icon: FaFileAlt,
      title: 'Medical Records',
      description: 'Access your surgical records, test results, and medical history',
      color: 'brand.500'
    },
    {
      icon: FaCalendarCheck,
      title: 'Appointments',
      description: 'Schedule, view, and manage your upcoming appointments',
      color: 'starOfLife.500'
    },
    {
      icon: FaPrescriptionBottleAlt,
      title: 'Prescriptions',
      description: 'View prescription history and request refills',
      color: 'purple.500'
    },
    {
      icon: FaMoneyBillWave,
      title: 'Billing',
      description: 'Review statements, make payments, and manage insurance',
      color: 'green.500'
    }
  ];

  const beforeYourVisit = [
    'Complete all pre-operative paperwork and medical history forms',
    'Arrange transportation - you will not be able to drive after surgery',
    'Follow pre-operative fasting instructions provided by your physician',
    'Bring your insurance card, photo ID, and list of current medications',
    'Wear comfortable, loose-fitting clothing',
  ];

  return (
    <Box>
      {/* Hero Section */}
      {/* Photographic hero, shared with every inner page. See
          components/premium/PageHero for why the photo is no longer
          hidden under a cyan gradient. */}
      <PageHero
        title={"Patient Portal"}
        subtitle={"Convenient access to your health information and appointment management"}
        image="/facility/exterior.jpg"
        imagePosition="center 55%"
      />

      {/* Coming Soon Notice */}
      <Container maxW="container.xl" mt={-10} position="relative" zIndex={2}>
        <Box
          {...card}
          p={{ base: 8, md: 10 }}
          textAlign="center"
        >
          <VStack spacing={4}>
            <IconMark icon={FaClock} size="lg" />
            <Heading size="lg" color="ink.900" fontWeight="600">
              Patient Portal Coming Soon
            </Heading>
            <Text color="ink.600" fontSize="lg" maxW="2xl">
              We're building a comprehensive patient portal to give you 24/7 access to your health information. Stay tuned for launch updates!
            </Text>
          </VStack>
        </Box>
      </Container>

      {/* Portal Features Section */}
      <Container maxW="container.xl" py={20}>
        <SectionHeading
          title="What's Coming to the Portal"
          lede="Our patient portal will provide secure, convenient access to all your health information"
          size="xl"
          mb={12}
        />

        <RevealGroup
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          gap={8}
        >
          {portalFeatures.map((feature, index) => (
            <RevealItem key={index}>
              <HStack
                {...card}
                {...cardHover}
                h="100%"
                p={8}
                spacing={6}
                align="start"
              >
                <IconMark icon={feature.icon} size="lg" />
                <VStack align="start" spacing={2}>
                  <Heading size="md" color="ink.900" fontWeight="600">
                    {feature.title}
                  </Heading>
                  <Text color="ink.600" lineHeight="tall">
                    {feature.description}
                  </Text>
                </VStack>
              </HStack>
            </RevealItem>
          ))}
        </RevealGroup>
      </Container>

      {/* Before Your Visit Section */}
      {/* The pale cyan wash is now the quiet mist surface. Deliberately a
          plain section and not a sliding sheet: these are pre-operative
          instructions, so the surface holds still while they are read. */}
      <Box bg="mist.50" py={{ base: 20, md: 28 }}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={12} alignItems="center">
            <GridItem>
              <VStack align="start" spacing={6}>
                <Heading
                  size="xl"
                  color="ink.900"
                  fontWeight="600"
                >
                  Before Your Visit
                </Heading>

                <Text fontSize="lg" color="ink.700">
                  To ensure a smooth surgical experience, please follow these important guidelines:
                </Text>

                {/* Deliberately a stagger and not the stacking-card
                    treatment used elsewhere: these are pre-operative safety
                    instructions, and stacking cards cover earlier items as
                    you scroll. Every line here has to stay readable. */}
                <Reveal>
                  <List spacing={4} w="100%">
                  {beforeYourVisit.map((item, index) => (
                    <ListItem
                      key={index}
                      bg="white"
                      p={4}
                      borderRadius="16px"
                      border="1px solid"
                      borderColor={HAIRLINE}
                      boxShadow="cardSoft"
                    >
                      <HStack align="start" spacing={3}>
                        <ListIcon as={FaExclamationCircle} color="brand.600" boxSize={6} mt={1} />
                        <Text color="ink.700" fontSize="md">
                          {item}
                        </Text>
                      </HStack>
                    </ListItem>
                    ))}
                  </List>
                </Reveal>
              </VStack>
            </GridItem>

            <GridItem>
              <Reveal>
                <Box
                  {...card}
                  p={10}
                >
                  <VStack spacing={6} align="stretch">
                    <Heading size="lg" color="ink.900" fontWeight="600">
                      Need Assistance?
                    </Heading>

                    <Text color="ink.600" fontSize="lg">
                      Our dedicated care coordinators are here to help you prepare for your visit and answer any questions.
                    </Text>

                    <VStack spacing={4} pt={4}>
                      <Button
                        as="a"
                        href="tel:+13213213935"
                        w="100%"
                        size="lg"
                        variant="brandGradient"
                        leftIcon={<Icon as={FaChevronRight} />}
                      >
                        Call (321) 321-3935
                      </Button>

                      <Button
                        as={RouterLink}
                        to="/contact"
                        w="100%"
                        size="lg"
                        variant="outline"
                        borderColor="rgba(14,47,58,0.22)"
                        color="ink.900"
                        fontWeight="600"
                        transition="transform 0.5s cubic-bezier(0.16,1,0.3,1), background-color 0.2s"
                        _hover={{
                          bg: 'mist.100',
                          transform: 'translateY(-2px)',
                        }}
                        rightIcon={<FaChevronRight />}
                      >
                        Send Us a Message
                      </Button>
                    </VStack>
                  </VStack>
                </Box>
              </Reveal>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* Insurance Section */}
      <Container maxW="container.xl" py={20}>
        <Reveal>
          <Box
            {...card}
            p={{ base: 8, md: 12 }}
            textAlign="center"
          >
            <VStack spacing={6}>
              <IconMark icon={FaMoneyBillWave} size="lg" />
              <Heading size="xl" color="ink.900" fontWeight="600">
                Insurance & Billing
              </Heading>
              <Text fontSize="lg" color="ink.600" maxW="3xl">
                We accept most major insurance plans. Please contact our billing department to verify your coverage and discuss payment options. Our team is committed to making quality surgical care accessible and affordable.
              </Text>
              <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
                {/* Estimate first, in amber — the same standout treatment the
                    link gets in the header and on the home hero. */}
                <Button
                  as={RouterLink}
                  to="/price-transparency"
                  size="lg"
                  // Amber stays, since the estimate is deliberately the one
                  // non-blue action. Solid rather than a gradient, and no
                  // orange glow, to match the header's estimate button.
                  bg="accent.400"
                  color="ink.900"
                  fontWeight="700"
                  borderRadius="pill"
                  boxShadow="inset 0 1px 0 rgba(255,255,255,0.35)"
                  _hover={{
                    bg: 'accent.300',
                    transform: 'translateY(-2px)',
                  }}
                  transition="transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.2s"
                  rightIcon={<FaChevronRight />}
                >
                  Get a Cost Estimate
                </Button>
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  variant="brandGradient"
                  rightIcon={<FaChevronRight />}
                >
                  Contact Billing
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Reveal>
      </Container>

      {/* CTA Section */}
      {/* Was a cyan-to-blue gradient panel with a blurred glow; now the deep
          ink band that closes every inner page. */}
      <InkBand image="/facility/exterior.jpg" imageOpacity={0.16}>
        <Container maxW="container.xl">
          <Reveal textAlign="center">
            <VStack spacing={6}>
              <Heading size="xl" color="white" fontWeight="600">
                Questions About Your Upcoming Procedure?
              </Heading>
              <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl">
                Our team is available to address any concerns and help you feel confident and prepared
              </Text>
              <HStack spacing={4} pt={4}>
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  px={8}
                  py={7}
                  variant="brandOnDark"
                  fontSize="lg"
                >
                  Contact Us
                </Button>
              </HStack>
            </VStack>
          </Reveal>
        </Container>
      </InkBand>
    </Box>
  );
};

export default PatientPortal;
